import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { faPen, faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck, faCirclePlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, DatePicker, InputText, SingleSelectInput, Text } from "@performyard/py-components";

import { Single as SingleSelectEmployee } from "../../../components/Inputs/SelectEmployee/Single";

import type { EditableGoalFields, GoalSuggestionState } from "../types";

import { getStrategyCategories } from "../../../api/services";
import { fetchEmployees } from "../../../api/services";

import { getUserIsAdmin } from "../../../selectors/app";
import { getRequireGoalCategory } from "../../../selectors/settings";

import { MeasureCanvas } from "../../../utils/textMeasurement/MeasureCanvas";
import { formatTargetWithUnits } from "../utils";
import { validateGoalFields } from "../utils/validateGoalFields";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type GoalEditCardProps = {
	suggestionState: GoalSuggestionState;
	onCreateGoal: (editedValues: EditableGoalFields) => void;
	onDeleteGoal: () => void;
	isCreating: boolean;
};

export const GoalEditCard: React.FC<GoalEditCardProps> = ({
	suggestionState,
	onCreateGoal,
	onDeleteGoal,
	isCreating,
}) => {
	const hasGoalCategories = useAskPerformYardStore((state) => state.hasGoalCategories);
	const setEditedGoalFields = useAskPerformYardStore((state) => state.setEditedGoalFields);
	const validationTrigger = useAskPerformYardStore((state) => state.validationTrigger);
	const isAdmin = useSelector(getUserIsAdmin);
	const requireCategory = useSelector(getRequireGoalCategory);
	const { suggestion, status } = suggestionState;
	const categoryOptionsRef = useRef<Array<{ label: string; value: string }>>([]);

	const fetchGoalAssignees = useCallback(async (searchTerm: string) => {
		const data = await fetchEmployees({
			searchTerm,
			filters: { canAssignGoalsTo: "true" },
		});
		return data.users?.map((user) => ({ id: user._id, name: user.name })) || [];
	}, []);

	const [formState, setFormState] = useState<EditableGoalFields>(() => ({
		name: suggestion.name,
		target: String(suggestion.target),
		units: suggestion.units,
		startDate: suggestion.startDate ? new Date(suggestion.startDate + "T00:00:00") : null,
		endDate: suggestion.endDate ? new Date(suggestion.endDate + "T00:00:00") : null,
		categories: suggestion.categories.map((c) => ({ _id: c.id, name: c.name })),
		assignee: { id: suggestion.assigneeId, name: suggestion.assigneeName },
	}));

	const [isEditingName, setIsEditingName] = useState(false);
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
	const idPrefix = suggestion.id;

	// Dynamic input width matching the form builder pattern
	const nameInputWidth = useMemo(() => {
		const canvas = new MeasureCanvas();
		return canvas.getTextWidth(formState.name || " ", "600 14px Source Sans Pro");
	}, [formState.name]);

	// Sync form state to store so handleCreateAllGoals can access edited values
	useEffect(() => {
		setEditedGoalFields(suggestion.id, formState);
	}, [formState, suggestion.id, setEditedGoalFields]);

	// Run validation when "Create All Goals" triggers it
	useEffect(() => {
		if (validationTrigger > 0 && status === "pending") {
			const errors = validateGoalFields(formState);
			setValidationErrors(errors);
		}
	}, [validationTrigger, formState, status]);

	// Reset editing state when goal is created
	useEffect(() => {
		if (status === "created") setIsEditingName(false);
	}, [status]);

	if (status === "deleted") {
		return null;
	}

	const isCreated = status === "created";
	const isDisabled = isCreated || isCreating;

	const updateField = <K extends keyof EditableGoalFields>(key: K, value: EditableGoalFields[K]) => {
		setFormState((prev) => ({ ...prev, [key]: value }));
		setValidationErrors((prev) => {
			const next = { ...prev };
			delete next[key];
			return next;
		});
	};

	const validateForm = (): boolean => {
		const errors = validateGoalFields(formState);
		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleCreate = () => {
		if (validateForm()) {
			onCreateGoal(formState);
		}
	};

	return (
		<div className="flex min-w-0 flex-col gap-1">
			{/* Goal Card — bordered container */}
			<div className="flex min-w-0 flex-col gap-3 rounded-lg border border-solid border-neutral-3 p-3">
				{/* Goal Name */}
				<div className="group flex items-center gap-1">
					{!isCreated && isEditingName ? (
						<form
							className="min-w-0"
							onSubmit={(e) => {
								e.preventDefault();
								setIsEditingName(false);
							}}
							onBlur={(e) => {
								if (!e.currentTarget.contains(e.relatedTarget as Node)) {
									setIsEditingName(false);
								}
							}}
						>
							<input
								autoFocus
								className="max-w-full border-0 border-b-[0.5px] border-solid border-neutral-9 bg-transparent p-0 text-sm font-semibold text-neutral-9 outline-hidden hover:cursor-text focus:border-primary-4 active:border-primary-4"
								style={{ width: nameInputWidth, minWidth: "100px" }}
								value={formState.name}
								onChange={(e) => updateField("name", e.target.value)}
								disabled={isDisabled}
								id={`goal-name-${idPrefix}`}
							/>
						</form>
					) : (
						<span
							role="button"
							tabIndex={isDisabled ? -1 : 0}
							className="min-h-[20px] min-w-0 truncate border-0 border-b-[0.5px] border-solid border-transparent text-sm font-semibold text-neutral-9 group-hover:border-neutral-9 hover:cursor-text"
							onClick={() => !isDisabled && setIsEditingName(true)}
							onKeyDown={(e) => {
								if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
									e.preventDefault();
									setIsEditingName(true);
								}
							}}
						>
							{formState.name || "\u00A0"}
						</span>
					)}
					{!isDisabled && (
						<ButtonIcon
							icon={faPen}
							onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
							onClick={() => setIsEditingName((prev) => !prev)}
							variant="standard"
							color="neutral"
							shade={5}
							size={16}
							aria-label="Edit goal name"
						/>
					)}
				</div>
				{validationErrors.name && (
					<Text size="caption" color="red-6">
						{validationErrors.name}
					</Text>
				)}

				{/* Read-only detail fields (created state) */}
				{isCreated && (
					<div className="flex flex-col gap-1">
						<Text size="caption" color="neutral-9">
							Target: {formatTargetWithUnits(formState.target, formState.units)}
						</Text>
						<Text size="caption" color="neutral-9">
							Assignee: {formState.assignee?.name ?? suggestion.assigneeName}
						</Text>
						<Text size="caption" color="neutral-9">
							Start Date:{" "}
							{formState.startDate
								? formState.startDate.toLocaleDateString("en-US", {
										month: "short",
										day: "numeric",
										year: "numeric",
									})
								: "—"}
						</Text>
						<Text size="caption" color="neutral-9">
							End Date:{" "}
							{formState.endDate
								? formState.endDate.toLocaleDateString("en-US", {
										month: "short",
										day: "numeric",
										year: "numeric",
									})
								: "—"}
						</Text>
						{(requireCategory || formState.categories.length > 0) && hasGoalCategories && (
							<Text size="caption" color="neutral-9">
								Category: {formState.categories.map((c) => c.name).join(", ")}
							</Text>
						)}
					</div>
				)}

				{/* Editable fields (pending state) */}
				{!isCreated && (
					<div className="flex flex-col gap-2">
						{/* Target + Units row */}
						<div className="flex items-center gap-2">
							<div className="flex min-w-0 flex-1 items-center gap-1">
								<Text
									size="caption"
									weight="semi-bold"
									color="neutral-9"
									dangerouslySetClassName="w-[52px] shrink-0 leading-6"
								>
									Target
								</Text>
								<div className="min-w-0 flex-1">
									<InputText
										size="small"
										value={formState.target}
										onChange={(value: string) => updateField("target", value)}
										disabled={isDisabled}
										id={`target-${idPrefix}`}
										aria-label="Goal target"
									/>
								</div>
							</div>
							<div className="flex min-w-0 flex-1 items-center gap-1">
								<Text
									size="caption"
									weight="semi-bold"
									color="neutral-9"
									dangerouslySetClassName="w-[28px] shrink-0 leading-6"
								>
									Units
								</Text>
								<div className="min-w-0 flex-1">
									<InputText
										size="small"
										value={formState.units}
										onChange={(value: string) => updateField("units", value)}
										disabled={isDisabled}
										id={`units-${idPrefix}`}
										aria-label="Goal units"
									/>
								</div>
							</div>
						</div>
						{validationErrors.target && (
							<Text size="caption" color="red-6">
								{validationErrors.target}
							</Text>
						)}

						{/* Assignee */}
						<div className="flex items-center gap-1">
							<Text
								size="caption"
								weight="semi-bold"
								color="neutral-9"
								dangerouslySetClassName="w-[52px] shrink-0"
							>
								Assignee
							</Text>
							<div className="min-w-0 flex-1">
								{isAdmin ? (
									<SingleSelectEmployee
										size="small"
										value={formState.assignee ?? null}
										onChange={(employeeId, employeeName) => {
											if (employeeId && employeeName) {
												updateField("assignee", {
													id: employeeId,
													name: employeeName,
												});
											} else {
												updateField("assignee", null);
											}
										}}
										disabled={isDisabled}
										fetchEmployees={fetchGoalAssignees}
										isClearable={false}
									/>
								) : (
									<Text size="caption" color="neutral-9">
										{formState.assignee?.name ?? suggestion.assigneeName}
									</Text>
								)}
							</div>
						</div>
						{validationErrors.assignee && (
							<Text size="caption" color="red-6">
								{validationErrors.assignee}
							</Text>
						)}

						{/* Start Date */}
						<div className="flex items-center gap-1">
							<Text
								size="caption"
								weight="semi-bold"
								color="neutral-9"
								dangerouslySetClassName="w-[52px] shrink-0"
							>
								Start Date
							</Text>
							<div className="min-w-0 flex-1">
								<DatePicker
									size="small"
									allowEmpty
									value={formState.startDate}
									onChange={(value: Date | null) => updateField("startDate", value)}
									disabled={isDisabled}
									id={`start-date-${idPrefix}`}
									popoverClassName="z-[1400]"
								/>
							</div>
						</div>
						{validationErrors.startDate && (
							<Text size="caption" color="red-6">
								{validationErrors.startDate}
							</Text>
						)}

						{/* End Date */}
						<div className="flex items-center gap-1">
							<Text
								size="caption"
								weight="semi-bold"
								color="neutral-9"
								dangerouslySetClassName="w-[52px] shrink-0"
							>
								End Date
							</Text>
							<div className="min-w-0 flex-1">
								<DatePicker
									size="small"
									allowEmpty
									value={formState.endDate}
									onChange={(value: Date | null) => updateField("endDate", value)}
									disabled={isDisabled}
									id={`due-date-${idPrefix}`}
									popoverClassName="z-[1400]"
								/>
							</div>
						</div>
						{validationErrors.endDate && (
							<Text size="caption" color="red-6">
								{validationErrors.endDate}
							</Text>
						)}

						{/* Category — only shown when the company has goal categories configured */}
						{(requireCategory || formState.categories.length > 0) && hasGoalCategories && (
							<div className="flex items-center gap-1">
								<Text
									size="caption"
									weight="semi-bold"
									color="neutral-9"
									dangerouslySetClassName="w-[52px] shrink-0"
								>
									Category
								</Text>
								<div className="min-w-0 flex-1">
									<SingleSelectInput
										size="small"
										placeholder="Select a category"
										disabled={isDisabled}
										isClearable={!requireCategory}
										selectionVariant="combobox"
										fetchOptions={async (searchTerm) => {
											const { categories } = await getStrategyCategories();
											const active = categories.filter((c) => !c.deleted);
											const options = active.map((c) => ({
												label: c.name,
												value: c._id,
											}));
											categoryOptionsRef.current = options;
											return searchTerm
												? options.filter((o) =>
														String(o.label).toLowerCase().includes(searchTerm.toLowerCase())
													)
												: options;
										}}
										value={
											formState.categories.length > 0 ? formState.categories[0]._id : undefined
										}
										renderValue={() =>
											formState.categories.length > 0 ? formState.categories[0].name : ""
										}
										onChange={(value) => {
											if (value) {
												const option = categoryOptionsRef.current.find(
													(o) => o.value === value
												);
												updateField("categories", [
													{ _id: value, name: option ? String(option.label) : "" },
												]);
											} else {
												updateField("categories", []);
											}
										}}
									/>
								</div>
							</div>
						)}
					</div>
				)}
			</div>

			{/* Action Bar — outside the bordered card */}
			<div className="flex items-center justify-between">
				<div className="flex flex-1 items-center gap-2">
					{!isCreated && (
						<ButtonIcon
							icon={faTrashCan}
							onClick={onDeleteGoal}
							variant="standard"
							color="neutral"
							shade={5}
							size={16}
							disabled={isCreating}
							aria-label="Delete goal suggestion"
						/>
					)}
				</div>
				{isCreated ? (
					<ButtonStandard
						variant="text"
						color="green"
						size="medium"
						iconLeft={faCircleCheck}
						onClick={() => {
							if (suggestionState.createdGoalId) {
								window.open(`/goal/${suggestionState.createdGoalId}/`, "_blank");
							}
						}}
						dangerouslySetClassName="!w-auto"
					>
						Goal Created
					</ButtonStandard>
				) : (
					<ButtonStandard
						variant="text"
						color="primary"
						size="medium"
						iconLeft={faCirclePlus}
						onClick={handleCreate}
						disabled={isCreating}
						dangerouslySetClassName="!w-auto"
					>
						{isCreating ? "Creating..." : "Create Goal"}
					</ButtonStandard>
				)}
			</div>
		</div>
	);
};

export default GoalEditCard;
